import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { Sparkles, X, PlayCircle, Rocket, BookOpen, ListChecks, ChevronDown, LifeBuoy } from 'lucide-react';
import AskBosun from './AskBosun';
import {
  getBasicTourSteps,
  getAdvancedTourSteps,
  getGuides,
  getIntelGuides,
  filterStepsToDom,
  BASIC_TOUR_ID,
  ADVANCED_TOUR_ID,
  INTEL_TOUR_PENDING_KEY,
} from '../lib/tours';

const LS_BASIC_DONE = 'bf_tour_basic_done';
const LS_ADV_DONE = 'bf_tour_advanced_done';
const LS_ADV_HINTED = 'bf_tour_advanced_hinted';
const LS_LOGIN_COUNT = 'bf_dashboard_visits';
const LS_WELCOME_DONE = 'bf_welcome_setup_shown';   // visit 1: setup guide pops once
const LS_LAUNCHER_HINTED = 'bf_launcher_hint_shown'; // "we'll be right here" coach mark
const LS_BASIC_PROMPTED = 'bf_tour_basic_prompted';  // visit 3 nudge fired
const LS_ADV_PROMPTED = 'bf_tour_advanced_prompted'; // visit 7 nudge fired
const LS_GUIDES_PROMPTED = 'bf_tour_guides_prompted'; // visit 10 nudge fired
const ADV_HINT_AFTER_VISITS = 3; // subtle "new" dot appears after a few visits
const BASIC_PROMPT_VISIT = 3;
const ADV_PROMPT_VISIT = 7;
const GUIDES_PROMPT_VISIT = 10;

// Dispatch tab/group/settings changes BEFORE driver.js moves to the step, so
// the accordion opens the right group, dialogs mount, and content switches
// cleanly. Returns the delay (ms) to wait before moving.
const IS_MOBILE = () => window.innerWidth < 1024;

// Does this step highlight something inside the sidebar?
const stepInSidebar = (step) => {
  if (!step?.element) return false;
  let el = null;
  try {
    el = typeof step.element === 'function' ? step.element() : document.querySelector(step.element);
  } catch (e) { el = null; }
  if (el) return !!el.closest('[data-tour-sidebar]');
  return typeof step.element === 'string' && step.element.includes('nav-');
};

const prepStep = (step) => {
  if (!step) return 0;
  let delay = 0;
  if (step.group) { window.dispatchEvent(new CustomEvent('tour-set-group', { detail: step.group })); delay = 220; }
  if (step.view) { window.dispatchEvent(new CustomEvent('navigate', { detail: step.view })); delay = 220; }
  if (step.openSettings) { window.dispatchEvent(new CustomEvent('openSettings')); delay = 700; }
  if (step.closeSettings) { window.dispatchEvent(new CustomEvent('closeSettings')); delay = 500; }
  if (step.settingsTab) { window.dispatchEvent(new CustomEvent('settings-set-tab', { detail: step.settingsTab })); delay = Math.max(delay, 320); }
  // Mobile: the sidebar is a fixed overlay — keep it open only while a step
  // actually points at it, otherwise it covers the highlighted content.
  if (IS_MOBILE()) {
    window.dispatchEvent(new CustomEvent('tour-sidebar', { detail: stepInSidebar(step) }));
    delay = Math.max(delay, 380);
  }
  return delay;
};

/**
 * Floating ✨ launcher tab on the right edge. Opens a small menu to Ask Bosun,
 * run guided tours, or open Help.
 *
 * Props:
 *  - mode?: 'dashboard' | 'intelligence'   (which suite the launcher lives in)
 *  - onOpenHelp?: () => void
 *  - prepareForTour?: () => void            // open sidebar + expand groups so anchors exist
 *  - hasAdvanced?: boolean                  // show the Advanced tour option
 *  - isAdmin?: boolean                      // admin-only steps (webhooks/API keys)
 *  - currentView?: string                   // active tab id (context for Ask Bosun)
 *  - onStartIntelTour?: () => void          // intelligence mode: run the suite tour
 */
const TourLauncher = ({ onOpenHelp, onOpenSetup, setupStatus = null, prepareForTour, hasAdvanced = true, isAdmin = false, currentView, mode = 'dashboard', onStartIntelTour, canAccessView = () => true }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const [bosunOpen, setBosunOpen] = useState(false);
  const [showAdvHint, setShowAdvHint] = useState(false);
  const [showLauncherHint, setShowLauncherHint] = useState(false);
  const [showGuidesNudge, setShowGuidesNudge] = useState(false);
  const driverRef = useRef(null);
  const autoLaunchRanRef = useRef(false);

  // Role-aware guide list: only offer guides whose every tab the user can open.
  const visibleGuides = getGuides().filter((g) =>
    (g.steps || []).every((st) => !st.view || canAccessView(st.view))
  );

  const runTour = useCallback((which) => {
    setMenuOpen(false);
    setGuidesOpen(false);
    setBosunOpen(false);
    // Never stack tours: tear down any active driver instance first (covers
    // re-renders, remounts, and rapid manual launches).
    try {
      if (window.__bfActiveDriver?.isActive?.()) window.__bfActiveDriver.destroy();
    } catch (e) { /* stale instance — ignore */ }
    window.__bfTourActive = true; // set BEFORE the mount delay so a racing auto-launch can't double-fire
    // Open the sidebar (mobile) and expand every nav group so ALL permitted
    // anchors exist in the DOM for the RBAC-aware step filter below.
    if (prepareForTour) prepareForTour();
    setTimeout(() => {
      const rawSteps = which === ADVANCED_TOUR_ID
        ? getAdvancedTourSteps(isAdmin)
        : which === BASIC_TOUR_ID
          ? getBasicTourSteps()
          : (getGuides().find((g) => g.id === which)?.steps || []);
      const steps = filterStepsToDom(rawSteps);
      if (steps.length === 0) { window.__bfTourActive = false; return; }

      const visibleCount = (sel) =>
        Array.from(document.querySelectorAll(sel)).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0).length;

      // A step's anchor may mount a beat after its tab switch — wait for it
      // (up to ~3s) before moving, so highlights never fall back to center.
      const stepReady = (step) => {
        if (!step || !step.element) return true;
        if (typeof step.element === 'function') {
          const el = step.element();
          return !!el && (el.offsetWidth > 0 || el.offsetHeight > 0);
        }
        return visibleCount(step.element) > 0;
      };
      const moveWhenReady = (drv, next, delay, attempts = 10) => {
        setTimeout(() => {
          if (!stepReady(next) && attempts > 0) return moveWhenReady(drv, next, 300, attempts - 1);
          if (!stepReady(next)) console.warn('[tour] anchor never resolved — centered fallback:', next?.popover?.title);
          drv.moveNext();
        }, delay);
      };

      let watcher = null;
      const d = driver({
        showProgress: true,
        allowClose: true,
        overlayColor: 'rgba(15, 23, 42, 0.72)',
        stagePadding: 6,
        stageRadius: 10,
        popoverClass: 'bf-tour-popover',
        nextBtnText: 'Next →',
        prevBtnText: '← Back',
        doneBtnText: 'Done',
        steps,
        // Hands-on steps (advanceOn / advanceOnSelector) hide Next — the user's
        // real action moves the tour along.
        onPopoverRender: (popover, opts) => {
          const st = opts?.state?.activeStep;
          if ((st?.advanceOn || st?.advanceOnSelector) && popover?.nextButton) {
            popover.nextButton.style.display = 'none';
          }
        },
        // Accordion navigation: open the target group / switch tab / open the
        // Settings dialog, wait a beat for the DOM, THEN move — so the
        // highlight always lands on a real, visible element.
        onNextClick: () => {
          const drv = driverRef.current;
          if (!drv) return;
          const idx = drv.getActiveIndex() ?? 0;
          const next = steps[idx + 1];
          moveWhenReady(drv, next, Math.max(prepStep(next), 60));
        },
        onPrevClick: () => {
          const drv = driverRef.current;
          if (!drv) return;
          const idx = drv.getActiveIndex() ?? 0;
          const prev = steps[idx - 1];
          let delay = prepStep(prev);
          // Moving back INTO the settings sequence needs the dialog reopened;
          // moving back OUT of it needs the dialog closed.
          if (prev?.settingsTab && !prev.openSettings && steps[idx]?.closeSettings) {
            window.dispatchEvent(new CustomEvent('openSettings'));
            delay = Math.max(delay, 450);
          }
          if (steps[idx]?.openSettings) {
            window.dispatchEvent(new CustomEvent('closeSettings'));
            delay = Math.max(delay, 450);
          }
          setTimeout(() => drv.movePrevious(), delay);
        },
        onDestroyed: (element, step) => {
          if (watcher) clearInterval(watcher);
          window.__bfTourActive = false;
          window.dispatchEvent(new CustomEvent('closeSettings'));
          if (IS_MOBILE()) window.dispatchEvent(new CustomEvent('tour-sidebar', { detail: false }));
          if (which === ADVANCED_TOUR_ID || which === BASIC_TOUR_ID) {
            localStorage.setItem(which === ADVANCED_TOUR_ID ? LS_ADV_DONE : LS_BASIC_DONE, '1');
          }
          if (which === ADVANCED_TOUR_ID) {
            localStorage.setItem(LS_ADV_HINTED, '1');
            setShowAdvHint(false);
            // Final step hands off into the Intelligence Suite briefing tour.
            if (step?.navigateToIntel) {
              sessionStorage.setItem(INTEL_TOUR_PENDING_KEY, '1');
              navigate('/intelligence');
            }
          }
        },
      });
      driverRef.current = d;
      window.__bfActiveDriver = d;

      // Watch hands-on steps: when the awaited element appears (the user really
      // did the thing), sail on automatically.
      let watchedIdx = -1;
      let baseline = 0;
      let advancingFrom = -1; // guard: one advance per step, no double-fires
      watcher = setInterval(() => {
        if (!d.isActive()) { clearInterval(watcher); return; }
        const idx = d.getActiveIndex() ?? 0;
        if (advancingFrom === idx) return; // advance already scheduled from this step
        if (advancingFrom !== -1) advancingFrom = -1; // step changed — reset guard
        const step = steps[idx];
        if (!step?.advanceOnSelector) { watchedIdx = -1; return; }
        if (watchedIdx !== idx) {
          watchedIdx = idx;
          baseline = step.advanceOnAdded ? visibleCount(step.advanceOnSelector) : 0;
          return;
        }
        const n = visibleCount(step.advanceOnSelector);
        const done = step.advanceOnAdded ? n > baseline : n > 0;
        if (done) {
          advancingFrom = idx;
          watchedIdx = -1;
          const next = steps[idx + 1];
          moveWhenReady(d, next, Math.max(prepStep(next), 650));
        }
      }, 450);

      if (IS_MOBILE()) {
        // Settle the sidebar for the first step before the curtain rises.
        window.dispatchEvent(new CustomEvent('tour-sidebar', { detail: stepInSidebar(steps[0]) }));
        setTimeout(() => d.drive(), 380);
      } else {
        d.drive();
      }
    }, 320);
  }, [prepareForTour, navigate, isAdmin]);

  // Paced onboarding (dashboard only). Visit 1: the Setup guide takes the
  // stage once, and when it closes a coach mark points at this launcher.
  // Visit 3: Basic tour (once, if untaken). Visit 7: Advanced tour (once).
  // Visit 10: crack the menu open on the how-to guides (once). After each
  // nudge fires we go quiet — help stays stashed on the right edge.
  useEffect(() => {
    if (mode !== 'dashboard') return;
    if (!autoLaunchRanRef.current) {
      autoLaunchRanRef.current = true;
      const v = parseInt(localStorage.getItem(LS_LOGIN_COUNT) || '0', 10) + 1;
      localStorage.setItem(LS_LOGIN_COUNT, String(v));
    }
    const visits = parseInt(localStorage.getItem(LS_LOGIN_COUNT) || '0', 10);
    const flag = (k) => localStorage.getItem(k) === '1';

    if (!flag(LS_WELCOME_DONE)) {
      // Genuinely new operators only — long-time users just get the flag set.
      if (visits <= 1 && onOpenSetup) {
        // Flag is written INSIDE the timer: a cancelled schedule (effect
        // re-run) must not burn the one-shot before the modal ever opened.
        const t = setTimeout(() => {
          if (window.__bfTourActive) return;
          localStorage.setItem(LS_WELCOME_DONE, '1');
          onOpenSetup();
        }, 900);
        const onClosed = () => {
          if (flag(LS_LAUNCHER_HINTED)) return;
          localStorage.setItem(LS_LAUNCHER_HINTED, '1');
          setShowLauncherHint(true);
        };
        window.addEventListener('bf-help-closed', onClosed, { once: true });
        return () => { clearTimeout(t); window.removeEventListener('bf-help-closed', onClosed); };
      }
      localStorage.setItem(LS_WELCOME_DONE, '1');
      return;
    }
    if (visits <= 1 && !flag(LS_LAUNCHER_HINTED)) {
      // Setup guide still open from the welcome pass — keep listening for it.
      const onClosed = () => {
        localStorage.setItem(LS_LAUNCHER_HINTED, '1');
        setShowLauncherHint(true);
      };
      window.addEventListener('bf-help-closed', onClosed, { once: true });
      return () => window.removeEventListener('bf-help-closed', onClosed);
    }

    if (visits >= BASIC_PROMPT_VISIT && !flag(LS_BASIC_DONE) && !flag(LS_BASIC_PROMPTED)) {
      const t = setTimeout(() => {
        if (window.__bfTourActive) return;
        localStorage.setItem(LS_BASIC_PROMPTED, '1');
        runTour(BASIC_TOUR_ID);
      }, 1200);
      return () => clearTimeout(t);
    }
    if (hasAdvanced && visits >= ADV_PROMPT_VISIT && !flag(LS_ADV_DONE) && !flag(LS_ADV_PROMPTED)) {
      const t = setTimeout(() => {
        if (window.__bfTourActive) return;
        localStorage.setItem(LS_ADV_PROMPTED, '1');
        runTour(ADVANCED_TOUR_ID);
      }, 1200);
      return () => clearTimeout(t);
    }
    if (visits >= GUIDES_PROMPT_VISIT && !flag(LS_GUIDES_PROMPTED)) {
      const t = setTimeout(() => {
        if (window.__bfTourActive) return;
        localStorage.setItem(LS_GUIDES_PROMPTED, '1');
        setShowGuidesNudge(true);
        setGuidesOpen(true);
        setMenuOpen(true);
      }, 1500);
      return () => clearTimeout(t);
    }
    if (
      hasAdvanced &&
      visits >= ADV_HINT_AFTER_VISITS &&
      localStorage.getItem(LS_ADV_DONE) !== '1' &&
      localStorage.getItem(LS_ADV_HINTED) !== '1'
    ) {
      setShowAdvHint(true);
    }
  }, [runTour, hasAdvanced, mode, onOpenSetup]);

  // Coach mark auto-dismisses after a while.
  useEffect(() => {
    if (!showLauncherHint) return;
    const t = setTimeout(() => setShowLauncherHint(false), 15000);
    return () => clearTimeout(t);
  }, [showLauncherHint]);

  return (
    <>
      {/* Floating launcher — circle in the bottom-right corner */}
      <button
        type="button"
        data-testid="tour-launcher-button"
        onClick={() => { setMenuOpen((o) => !o); if (showAdvHint) { localStorage.setItem(LS_ADV_HINTED, '1'); } }}
        aria-label="Help & guided tours"
        className="fixed bottom-5 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-[#1F2A5C] text-white shadow-[0_8px_24px_rgba(31,42,92,0.4)] transition-transform hover:scale-105 hover:bg-[#28356f] focus:outline-none"
      >
        <Sparkles className="h-6 w-6 text-[#F5B92E]" />
        {showAdvHint && (
          <span data-testid="tour-advanced-hint-dot" className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-[#F5B92E] ring-2 ring-white animate-pulse" />
        )}
      </button>

      {/* One-time coach mark after the first-visit setup guide closes */}
      {showLauncherHint && !menuOpen && (
        <div
          data-testid="launcher-coach-mark"
          className="fixed bottom-24 right-5 z-[62] w-64 max-w-[75vw] rounded-2xl bg-[#1F2A5C] p-4 text-white shadow-2xl"
        >
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            <Sparkles className="h-4 w-4 text-[#F5B92E]" /> We&rsquo;ll be right here
          </p>
          <p className="mt-1 text-xs leading-relaxed text-blue-200">
            Your setup checklist, guided tours, how-to guides and Bosun all live behind this button — tap it any time you need a hand.
          </p>
          <button
            data-testid="launcher-coach-dismiss"
            onClick={() => setShowLauncherHint(false)}
            className="mt-2.5 rounded-full bg-[#F5B92E] px-3 py-1 text-xs font-bold text-[#1F2A5C]"
          >
            Got it
          </button>
          <span className="absolute -bottom-1.5 right-7 h-3 w-3 rotate-45 bg-[#1F2A5C]" aria-hidden="true" />
        </div>
      )}

      {/* Menu popover */}
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setMenuOpen(false)} data-testid="tour-menu-overlay" />
          <div
            data-testid="tour-menu"
            className="fixed bottom-24 right-4 z-[61] w-72 max-w-[85vw] max-h-[70vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-2xl"
          >
            <div className="flex items-center justify-between px-2 py-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#F5B92E]" />
                <span className="text-sm font-semibold text-gray-900">Help &amp; Tours</span>
              </div>
              <button onClick={() => setMenuOpen(false)} aria-label="Close" data-testid="tour-menu-close" className="rounded-lg p-1 text-gray-400 hover:bg-gray-100">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Visit-10 nudge: the menu opened itself on the guides */}
            {showGuidesNudge && (
              <div data-testid="tour-guides-nudge" className="mx-2 mb-1 rounded-xl bg-[#F5B92E]/15 px-3 py-2 text-xs font-medium text-[#8a6400]">
                Psst — you haven&rsquo;t tried the hands-on guides yet. Each one walks you through a real task in a minute or two. 👇
              </div>
            )}

            {/* Setup checklist — pinned up top until every core step is done */}
            {mode === 'dashboard' && setupStatus && !setupStatus.loading && setupStatus.remainingCore > 0 && (
              <button
                data-testid="tour-menu-setup-row"
                onClick={() => { setMenuOpen(false); if (onOpenSetup) onOpenSetup(); }}
                className="mb-1 flex w-full items-start gap-3 rounded-xl bg-blue-50 px-3 py-3 text-left transition-colors hover:bg-blue-100"
              >
                <ListChecks className="mt-0.5 h-5 w-5 shrink-0 text-blue-600" />
                <span className="flex-1">
                  <span className="flex items-center justify-between text-sm font-semibold text-gray-900">
                    Finish setting up
                    <span className="text-xs font-bold text-blue-700">{setupStatus.completedCore}/{setupStatus.totalCore}</span>
                  </span>
                  <span className="mt-1.5 block h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
                    <span
                      className="block h-full rounded-full bg-blue-600 transition-all"
                      style={{ width: `${Math.round((setupStatus.completedCore / Math.max(setupStatus.totalCore, 1)) * 100)}%` }}
                    />
                  </span>
                  <span className="mt-1 block text-xs text-gray-500">
                    {setupStatus.remainingCore} step{setupStatus.remainingCore === 1 ? '' : 's'} to go before you can take real bookings.
                  </span>
                </span>
              </button>
            )}

            <button
              data-testid="ask-bosun-button"
              onClick={() => { setMenuOpen(false); setBosunOpen(true); }}
              className="flex w-full items-center gap-3 rounded-xl bg-[#1F2A5C] px-3 py-3 text-left transition-colors hover:bg-[#28356f]"
            >
              <img src="/bosun-avatar.png" alt="Bosun" className="h-9 w-9 rounded-full bg-white/10 ring-2 ring-[#F5B92E]/50 shrink-0" />
              <span>
                <span className="block text-sm font-semibold text-white">Ask Bosun</span>
                <span className="block text-xs text-blue-200">Your first mate — ask anything about the platform.</span>
              </span>
            </button>

            {mode === 'intelligence' ? (
              <>
                <button
                  data-testid="tour-start-intel"
                  onClick={() => { setMenuOpen(false); setBosunOpen(false); if (onStartIntelTour) onStartIntelTour(); }}
                  className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-blue-50"
                >
                  <Rocket className="mt-0.5 h-5 w-5 shrink-0 text-[#1F2A5C]" />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">Tour the Intelligence Suite</span>
                    <span className="block text-xs text-gray-500">Chartroom reports, audiences &amp; campaigns.</span>
                  </span>
                </button>

                {/* Intelligence how-to guides — collapsed into a single row */}
                <button
                  data-testid="tour-guides-toggle"
                  onClick={() => setGuidesOpen((o) => !o)}
                  className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-blue-50"
                >
                  <ListChecks className="mt-0.5 h-5 w-5 shrink-0 text-[#1F2A5C]" />
                  <span className="flex-1">
                    <span className="flex items-center justify-between text-sm font-medium text-gray-900">
                      How-to Guides
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${guidesOpen ? 'rotate-180' : ''}`} />
                    </span>
                    <span className="block text-xs text-gray-500">Chartroom, Beacon setup, campaigns.</span>
                  </span>
                </button>
                {guidesOpen && (
                  <div className="ml-4 border-l border-gray-100 pl-2">
                    {getIntelGuides().map((g) => (
                      <button
                        key={g.id}
                        data-testid={`guide-start-${g.id}`}
                        onClick={() => { setMenuOpen(false); setGuidesOpen(false); setBosunOpen(false); if (onStartIntelTour) onStartIntelTour(g.id); }}
                        className="flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-blue-50"
                      >
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F5B92E]" />
                        <span>
                          <span className="block text-sm font-medium text-gray-900">{g.title}</span>
                          <span className="block text-xs text-gray-500">{g.blurb}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Tours & how-to guides — consolidated under one row */}
                <button
                  data-testid="tour-guides-toggle"
                  onClick={() => setGuidesOpen((o) => !o)}
                  className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-blue-50"
                >
                  <ListChecks className="mt-0.5 h-5 w-5 shrink-0 text-[#1F2A5C]" />
                  <span className="flex-1">
                    <span className="flex items-center justify-between text-sm font-medium text-gray-900">
                      <span className="flex items-center gap-2">
                        Tours &amp; How-to Guides
                        {showAdvHint && <span className="rounded-full bg-[#F5B92E]/20 px-1.5 py-0.5 text-[10px] font-semibold text-[#8a6400]">New</span>}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${guidesOpen ? 'rotate-180' : ''}`} />
                    </span>
                    <span className="block text-xs text-gray-500">Guided tours and bite-size task walkthroughs.</span>
                  </span>
                </button>
                {guidesOpen && (
                  <div className="ml-4 border-l border-gray-100 pl-2">
                    <button
                      data-testid="tour-start-basic"
                      onClick={() => runTour(BASIC_TOUR_ID)}
                      className="flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-blue-50"
                    >
                      <PlayCircle className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
                      <span>
                        <span className="block text-sm font-medium text-gray-900">Take the Basic Tour</span>
                        <span className="block text-xs text-gray-500">The essentials — bookings, services, team, waivers.</span>
                      </span>
                    </button>
                    {hasAdvanced && (
                      <button
                        data-testid="tour-start-advanced"
                        onClick={() => runTour(ADVANCED_TOUR_ID)}
                        className="flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-blue-50"
                      >
                        <Rocket className="mt-0.5 h-4 w-4 shrink-0 text-[#1F2A5C]" />
                        <span>
                          <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
                            Advanced Features Tour
                            {showAdvHint && <span className="rounded-full bg-[#F5B92E]/20 px-1.5 py-0.5 text-[10px] font-semibold text-[#8a6400]">New</span>}
                          </span>
                          <span className="block text-xs text-gray-500">Reports, settings &amp; security, the Beacon, Intelligence.</span>
                        </span>
                      </button>
                    )}
                    {visibleGuides.map((g) => (
                      <button
                        key={g.id}
                        data-testid={`guide-start-${g.id}`}
                        onClick={() => runTour(g.id)}
                        className="flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-blue-50"
                      >
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#F5B92E]" />
                        <span>
                          <span className="block text-sm font-medium text-gray-900">{g.title}</span>
                          <span className="block text-xs text-gray-500">{g.blurb}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            <a
              data-testid="tour-get-support"
              href="/support"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMenuOpen(false)}
              className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-blue-50"
            >
              <LifeBuoy className="mt-0.5 h-5 w-5 shrink-0 text-[#1F2A5C]" />
              <span>
                <span className="block text-sm font-medium text-gray-900">Get Support</span>
                <span className="block text-xs text-gray-500">Search help articles or submit a support ticket.</span>
              </span>
            </a>

            {onOpenHelp && (
              <button
                data-testid="tour-open-help"
                onClick={() => { setMenuOpen(false); onOpenHelp(); }}
                className="flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition-colors hover:bg-blue-50"
              >
                <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-gray-600" />
                <span>
                  <span className="block text-sm font-medium text-gray-900">Open Help Docs</span>
                  <span className="block text-xs text-gray-500">Step-by-step guides for every feature.</span>
                </span>
              </button>
            )}
          </div>
        </>
      )}

      {/* Ask Bosun chat panel */}
      <AskBosun open={bosunOpen} onClose={() => setBosunOpen(false)} currentView={currentView} />
    </>
  );
};

export default TourLauncher;
